import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Container } from 'reactstrap';
import OrderTripView from './components/OrderTripView';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import LinearProgress from '@mui/material/LinearProgress';
import SnackbarContent from '@mui/material/SnackbarContent';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { showNotification } from 'src/shared/components/Notification';
import renderSelectField from 'src/shared/components/form/Select';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import renderMuiDateTimePickerField from 'src/shared/components/form/MuiDatePicker';

const OrderHistoryViewIndex = ({ formValues, reset }) => {
  useEffect(() => {
    getAllRidersDetails();
  }, [])
  const [reactTableData, setReactTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isNoRecordFound, setNoRecordFound] = useState(false);
  const [riderIds, setRiderIds] = useState([]);


  const getAllRidersDetails = async () => {
    let riderId = [];
    try {
      setLoading(true);
      const response = await PndRestApi.GetAllRidersDetails();
      if (response.status === 200 && response.data.status === 200) {
        response.data.data.map(item => {
          riderId.push({ "label": item.riderName, "value": item.riderId });
        })
        setRiderIds(riderId);
      } else {
        showNotification(
          "theme-light",
          "ltr",
          `ErrorCode: ${response.data.status}`,
          `Message:${response.data?.message}`,
        );

        return;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification(
        "theme-light",
        "ltr",
        `ErrorCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDateForBackend = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00.000Z`; // Set time to midnight UTC
  };

  const formatEndDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, '0');
    // Set to 23:59:59 UTC
    return `${year}-${month}-${day}T23:59:59.000Z`;
  };

  const searchBasedOnFilters = async () => {
    try {
      setLoading(true);
      setNoRecordFound(false);
      setReactTableData([]);
      if (formValues?.riderId?.value === undefined) {
        showNotification(
          "theme-light",
          "ltr",
          `Warning`,
          `Please select the required fields to search.`,
        );
      } else {
        let Obj = {
          riderId: formValues?.riderId?.value,
          fromDate: formValues?.fromDate ? formatDateForBackend(new Date(formValues.fromDate)) : null,
          toDate: formValues?.toDate ? formatEndDate(new Date(formValues.toDate)) : null,
        }

        if (Obj?.fromDate && Obj?.toDate && Obj?.riderId) {
          const response = await PndRestApi.GetTripHistory(Obj);
          if (response.status === 200 && response.data.status === 200) {
            setReactTableData({ tableHeaderData: columns, tableRowsData: response.data.data });
          } else if (response.status && response?.data?.status === 204) { //No Record Found
            setNoRecordFound(true);
            setReactTableData({ tableHeaderData: columns, tableRowsData: null });
          } else {
            showNotification(
              "theme-light",
              "ltr",
              `ErrorCode: 500`,
              ERROR_MESSAGES.tryCatchError,
            );
          }
        }
      }
    } catch (error) {
      console.log("error", error);
      showNotification(
        "theme-light",
        "ltr",
        `ErrorCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: '#',
        accessor: 'id1',
        disableGlobalFilter: true,
        width: 15,
      },
      {
        Header: 'Trip Start Time',
        accessor: 'departedDateTime',
        Cell: ({ value }) => new Date(value).toLocaleString(), // Format the date
      },
      {
        Header: 'Trip End Time',
        accessor: 'returnedDateTime',
        Cell: ({ value }) => new Date(value).toLocaleString(), // Format the date
      },
      {
        Header: 'Trip Duration',
        accessor: (row) => {
          const departed = new Date(row.departedDateTime);
          const returned = new Date(row.returnedDateTime);

          const diffTime = returned - departed; // Difference in milliseconds
          const diffMinutes = Math.floor(diffTime / (1000 * 60));
          const diffHours = Math.floor(diffMinutes / 60);
          const diffDays = Math.floor(diffHours / 24);

          return `${diffDays} days, ${diffHours % 24} hours, ${diffMinutes % 60} minutes`;
        },
      },
    ],
    []
  );


  const clearFilters = () => {
    reset();
    setReactTableData([]);
    setNoRecordFound(false);
  };

  return (
    <Container fluid className="px-2 py-2">
      <Card className="border-0 shadow-sm mb-3">
        <CardHeader className="bg-white py-2 border-bottom-0">
          <h6 className="mb-0 fw-bold text-primary">Rider Trip History Filters</h6>
        </CardHeader>
        <CardBody className="p-3">
          <form className="form" onSubmit={(e) => e.preventDefault()}>
            <div className="d-flex align-items-end flex-wrap gap-3">
              <div style={{ width: '220px' }}>
                <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.92rem' }}>From Date</label>
                <Field
                  name="fromDate"
                  component={renderMuiDateTimePickerField}
                  fullWidth
                  size="small"
                  margin="none"
                />
              </div>
              <div style={{ width: '220px' }}>
                <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.92rem' }}>To Date</label>
                <Field
                  name="toDate"
                  component={renderMuiDateTimePickerField}
                  fullWidth
                  size="small"
                  margin="none"
                />
              </div>
              <div style={{ width: '280px' }}>
                <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.92rem' }}>Driver Name</label>
                <Field
                  name="riderId"
                  component={renderSelectField}
                  type="text"
                  options={riderIds}
                  placeholder="Select Driver Name"
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="d-flex gap-2 mb-1">
                <Tooltip title="Clear Filters" arrow>
                  <IconButton
                    onClick={clearFilters}
                    size="small"
                    sx={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      '&:hover': { backgroundColor: '#e2e6ea' }
                    }}
                  >
                    <RestartAltIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Search Trip" arrow>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={searchBasedOnFilters}
                    disabled={loading}
                    sx={{
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': { backgroundColor: 'primary.dark' },
                      '&.Mui-disabled': { backgroundColor: '#ccc' }
                    }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardBody className="p-2">
          {loading && <LinearProgress color="primary" className="mb-2" />}
          {(reactTableData && reactTableData.tableRowsData != null) && (
            <div className="trip-history-results">
              <OrderTripView reactTableData={reactTableData} />
            </div>
          )}
          {isNoRecordFound && (
            <div className="p-4 text-center">
              <OrderTripView isNoRecordFound={isNoRecordFound} />
              <SnackbarContent message={"No Record Found"} sx={{ mt: 2, justifyContent: 'center', backgroundColor: '#f8d7da', color: '#721c24' }} />
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  formValues: state.form.OrderHistoryViewIndexForm ? state.form.OrderHistoryViewIndexForm.values : {},
});

export default connect(mapStateToProps)(reduxForm({ form: 'OrderHistoryViewIndexForm', enableReinitialize: true })(OrderHistoryViewIndex));